import {Key} from "./key";
import store from './store';
import ajax from './ajax';


function groupByParent(map, rawEntity) {
	let key;
	if (rawEntity.id) {
		key = rawEntity;
	} else {
		key = new Key(rawEntity.key);
	}
	let parentKey = key.parent || null;
	if (parentKey) {
		parentKey = parentKey.toString();
	}
	let table = map.get(parentKey);
	if (!table) {
		table = new Map();
		map.set(parentKey, table);
	}
	let children = table.get(key.kind);
	if (!children) {
		children = [];
		table.set(key.kind, children);
	}
	if (rawEntity.id) {
		children.push(rawEntity.id);
	} else {
		children.push(rawEntity);
	}
	return map;
}

function foreachGroupedByParent(map, callback) {
	for (let [parentKey, table] of map) {
		for (let [kind, rawEntities] of table) {
			callback(Key.fromString(parentKey), kind, rawEntities);
		}
	}
}

const eventsKeys = new Set();

class DatastoreEvent {
	constructor() {
		this.created = [];
		this.updated = [];
		this.deleted = [];
	}
}


/** Returns a Promise. */
function handleServerEvent(dbEvent) {
	const eventKey = (new Key(dbEvent.key)).toString();
	const storeEvent = new DatastoreEvent();

	if (!eventsKeys.has(eventKey)) {
		for (let action of ['created', 'updated']) {
			if (dbEvent[action]) {
				let toPut = new Map(); // Map<parentKey, Map<kind, rawEntities>>
				dbEvent[action].reduce(groupByParent, toPut);
				foreachGroupedByParent(toPut, (parentKey, kind, rawEntities) => {
                    storeEvent[action].push(...store.put(parentKey, kind, rawEntities));
				});
			}
		}

		if (dbEvent.deleted) {
			let toDelete = dbEvent.deleted.map((e) => new Key(e)).reduce(groupByParent, new Map());
			foreachGroupedByParent(toDelete, (parentKey, kind, ids) => {
                storeEvent.deleted.push(...store.delete(parentKey, kind, ids));
			});
		}

		eventsKeys.add(eventKey);
	}

	return Promise.resolve(storeEvent);
}

function createUrl(parentKey, kind, id) {
	let urlParts = ['/api'];
	if (parentKey) {
		urlParts = urlParts.concat(parentKey.data);
	}
	if (kind) {
		urlParts.push(kind);
	}
	if (id) {
		urlParts.push(id);
	}
	return urlParts.join('/');
}

const windowVisibility = (function () {
	let hidden = "hidden";

	// Standards:
	if (hidden in document)
		document.addEventListener("visibilitychange", onchange);
	else if ((hidden = "mozHidden") in document)
		document.addEventListener("mozvisibilitychange", onchange);
	else if ((hidden = "webkitHidden") in document)
		document.addEventListener("webkitvisibilitychange", onchange);
	else if ((hidden = "msHidden") in document)
		document.addEventListener("msvisibilitychange", onchange);
	// IE 9 and lower:
	else if ("onfocusin" in document)
		document.onfocusin = document.onfocusout = onchange;
	// All others:
	else
		window.onpageshow = window.onpagehide
			= window.onfocus = window.onblur = onchange;

	const visibility = {visible: true};

	function onchange(evt) {
		const evtMap = {
			focus: true, focusin: true, pageshow: true, blur: false, focusout: false, pagehide: false
		};

		evt = evt || window.event;
		if (evt.type in evtMap)
			visibility.visible = evtMap[evt.type];
		else
			visibility.visible = !this[hidden];
		$(visibility).trigger('change');
	}

	// set the initial state (but only if browser supports the Page Visibility API)
	if (document[hidden] !== undefined)
		onchange({type: document[hidden] ? "blur" : "focus"});

	return visibility;
}());


export class EntityWatcher {
	constructor(entityKey) {
		this.entityKey = entityKey;
		this.timer = null;
		this.running = false;
		this.started = false;
		this.paused = false;
		this.lastEventTime = null;

		this.afterPoll = this._afterPoll.bind(this);
		this.pollEvents = this._pollEvents.bind(this);
		this.handlerServerEvents = this._handlerServerEvents.bind(this);
		this.onWindowVisibilityChanged = this._onWindowVisibilityChanged.bind(this);
	}

	start() {
		this.started = true;
		// Get time of last event
		ajax.send(createUrl(this.entityKey, 'Event') + "?limit=1&order=-time", 'GET', null, {doNotNotify: true})
			.then((events) => {
				this.lastEventTime = events.length ? new Date(events[0].time) : new Date();
				this._afterPoll();
			});

		$(windowVisibility).on('change', this.onWindowVisibilityChanged);

		this._log();
		return this;
	}

	stop() {
		clearTimeout(this.timer);
		this.started = false;
		this._log();
		return this;
	}

	_log() {
		const now = new Date();
		console.debug('Watcher', this.entityKey.toString(), this.started ? 'started' : 'stopped', 'at',
			`${now.getHours()}:${now.getMinutes()}:${now.getSeconds()}`);
	}

	_handlerServerEvents(events) {
		events.forEach(handleServerEvent);
		if (events.length) {
			this.lastEventTime = new Date(events[events.length - 1].time);
		}
	}

	_afterPoll() {
		if (this.started && !this.paused) {
			this.timer = setTimeout(this.pollEvents, 30000);
		}
	}

	_pollEvents() {
		let url = [
			createUrl(this.entityKey, 'Event'),
			"?filter=", encodeURIComponent("time >," + this.lastEventTime.toISOString()),
			"&order=time"
		].join('');
		ajax.send(url, 'GET', null, {doNotNotify: true})
			.then(this.handlerServerEvents).then(this.afterPoll, this.afterPoll);
	}

	_onWindowVisibilityChanged() {
		if (this.started) {
			if (windowVisibility.visible) {
				this.paused = false;
				this._pollEvents();
				console.debug('Watcher resumed');
			} else {
				clearTimeout(this.timer);
				this.paused = true;
				console.debug('Watcher paused');
			}
		}
	}
}

const getPromises = new Map();

export default {
	/** Returns a Promise. */
	get(parentKey, kind, ids, limit, offset, orders) {
		const idsIsArray = ids instanceof Array;
		let urlParts = [createUrl(parentKey, kind, idsIsArray ? null : ids)];
		if (!idsIsArray) {
			if (limit) {
				urlParts.push('?limit=', limit, '&offset=', offset);
				if (orders) {
					for (let order of orders) {
						urlParts.push('&order=', order);
					}
				}
			}
		} else if (ids) {
			let first = true;
			for (let id of ids) {
				if (first) {
					urlParts.push('/?');
					first = false;
				} else {
					urlParts.push('&');
				}
				urlParts.push('id=', id);
			}
		}
		const url = urlParts.join('');
		let promise = getPromises.get(url);
		if (!promise) {
			promise = ajax.send(url, 'GET');
			const removePromise = () => {
				getPromises.delete(url)
			};
			promise.then(removePromise, removePromise);
			getPromises.set(url, promise);
		}
		return promise;
	},

	/** Returns a Promise. */
	post(parentKey, kind, entity) {
		return ajax.send(createUrl(parentKey, kind), 'POST', entity).then(handleServerEvent);
	},

	/** Returns a Promise. */
	put(entity) {
		return ajax.send(createUrl(entity.key), 'PUT', entity).then(handleServerEvent);
	},

	/** Returns a Promise. */
	delete(parentKey, kind, ids, options) {
		if (ids instanceof Array) {
			ids = ids.join('-');
		}
		const url = createUrl(parentKey, kind) + '/?ids=' + ids;
		return ajax.send(url, 'DELETE', null, options).then(handleServerEvent);
	}
}



// WEBPACK FOOTER //
// ./src/core/actions.js